import { format } from 'date-fns';

import type { BlotterOrderEntry } from '@/store/blotter/types/BlotterOrderEntry.ts';
import { getOMSCurrencyFromBlotterCurrency } from '@/store/orders/helpers.ts';
import type { ClientWay, OrderStateCurrencyPair } from '@/store/orders/types.ts';
import type { Order, OrderMode } from '@/store/types.ts';
import type { OmsErrors, OmsWarning } from '@/types/common.ts';
import type { Currency } from '@/types/currency.ts';
import type { CurrencyIndex } from '@/utils/currencies/types.ts';
import { getDateNow } from '@/utils/date.ts';

export type TwapOrder = Order<'Twap+Order', TwapOrderProperties>;

export type TwapOrderProperties = CommonAlgoOrderProperties & {
  clippingMode: ClippingMode;
  clipSize?: number;
  randomize: boolean; // dodging
  spreadCapture: boolean;
};
export type FalconOrder = Order<'FalconOrder', FalconOrderProperties>;

export type FalconOrderProperties = CommonAlgoOrderProperties & {
  speed: Speed;
};

export type NightjarOrder = Order<'NightjarOrder', NightjarOrderProperties>;

export type NightjarOrderProperties = CommonAlgoOrderProperties & {
  speed: Speed;
  alphaSeeker: boolean;
};
export type AlgoOrder = TwapOrder | FalconOrder | NightjarOrder;

export const speeds = ['VerySlow', 'Slow', 'Normal', 'Fast', 'VeryFast'] as const;
export type Speed = (typeof speeds)[number];

export type CommonAlgoOrderProperties = {
  status:
    | {
        type: 'pending';
      }
    | {
        type: 'creating';
      }
    | { type: 'failed'; errorMessage: string }
    | {
        type: 'submitted' | 'editing' | 'cancelled';
        tradeId: string;
      };
  isReadyToSubmit: boolean;
  clockOffset?: number;
  currencyPair?: OrderStateCurrencyPair;

  clientWay: ClientWay;
  notional?: number;
  notionalCurrencyIndex: CurrencyIndex;

  limitPrice?: number;
  isNdf: boolean;
  liquidityPool: string[];

  ndfFixingSource?: string;
  ndfFixingDate?: string;

  tenor?: string; // maturityDate
  startDate: StartDate;
  expiryDate: ExpiryDate; // isGtc

  // by default, use user preferences emails for order
  // override the default with this field
  notificationEmails?: string[];

  // to detect if the order is in EDIT or VIEW mode
  // EDIT mode is used when the user is editing an existing order
  // VIEW mode is used when the user is viewing an existing order
  mode: OrderMode;

  errors?: OmsErrors;
  warnings?: OmsWarning;
};

export type ExpiryDate = {
  goodTillDate: boolean; //is GTC
  date?: string; //endDateUtc
  time?: string;
};

export type StartDate = {
  isNow: boolean;
  date?: string; //startDateUtc
  time?: string;
};

export const mapTwap = (
  product: {
    type: 'Twap+Order';
    clientId?: number;
    properties: TwapOrderProperties;
    tileId: string;
  },
  defaultClientId: number,
): TwapOrderRequest => {
  return {
    orderType: 'Twap+Order',
    tileId: product.tileId,
    currency1: product.properties.currencyPair?.currency1 ?? '',
    currency2: product.properties.currencyPair?.currency2 ?? '',
    clientId: product.clientId ?? defaultClientId,
    clockOffset: product.properties.clockOffset,
    way: product.properties.clientWay,
    notional: product.properties.notional,
    notionalCurrencyIndex: product.properties.notionalCurrencyIndex,
    limitPrice: product.properties.limitPrice,
    isNdf: product.properties.isNdf,
    clipSize: product.properties.clipSize,
    spreadCapture: product.properties.spreadCapture,
    liquidityPool: product.properties.liquidityPool,
    clippingMode: product.properties.clippingMode,
    tenor: product.properties.tenor,
    startDate: product.properties.startDate,
    expiryDate: product.properties.expiryDate,
    randomize: product.properties.randomize,
    notificationEmails: {
      isOverridenEmails: false,
      emails: product.properties.notificationEmails ?? [],
    },
  };
};

export const mapNightjar = (
  product: {
    type: 'NightjarOrder';
    clientId?: number;
    properties: NightjarOrderProperties;
    tileId: string;
  },
  defaultClientId: number,
): NightjarOrderRequest => {
  return {
    orderType: product.type,
    tileId: product.tileId,
    currency1: product.properties.currencyPair?.currency1 ?? '',
    currency2: product.properties.currencyPair?.currency2 ?? '',
    clientId: product.clientId ?? defaultClientId,
    clockOffset: product.properties.clockOffset,
    way: product.properties.clientWay,
    notional: product.properties.notional,
    notionalCurrencyIndex: product.properties.notionalCurrencyIndex,
    limitPrice: product.properties.limitPrice,
    isNdf: product.properties.isNdf,
    liquidityPool: product.properties.liquidityPool,
    tenor: product.properties.tenor,
    startDate: product.properties.startDate,
    expiryDate: product.properties.expiryDate,
    alphaSeeker: product.properties.alphaSeeker,
    speed: product.properties.speed,
    notificationEmails: {
      isOverridenEmails: false,
      emails: product.properties.notificationEmails ?? [],
    },
  };
};

export const mapFalcon = (
  product: {
    type: 'FalconOrder';
    clientId?: number;
    properties: FalconOrderProperties;
    tileId: string;
  },
  defaultClientId: number,
): FalconOrderRequest => {
  return {
    orderType: product.type,
    tileId: product.tileId,
    currency1: product.properties.currencyPair?.currency1 ?? '',
    currency2: product.properties.currencyPair?.currency2 ?? '',
    clientId: product.clientId ?? defaultClientId,
    clockOffset: product.properties.clockOffset,
    way: product.properties.clientWay,
    notional: product.properties.notional,
    notionalCurrencyIndex: product.properties.notionalCurrencyIndex,
    limitPrice: product.properties.limitPrice,
    isNdf: product.properties.isNdf,
    liquidityPool: product.properties.liquidityPool,
    tenor: product.properties.tenor,
    startDate: product.properties.startDate,
    expiryDate: product.properties.expiryDate,
    speed: product.properties.speed,
    notificationEmails: {
      isOverridenEmails: false,
      emails: product.properties.notificationEmails ?? [],
    },
  };
};

export const generateTwapOrder = (): TwapOrder => ({
  type: 'Twap+Order',
  clientId: undefined,

  properties: {
    status: { type: 'creating' },
    mode: 'VIEW',
    clockOffset: undefined,
    currencyPair: undefined,
    isReadyToSubmit: false,

    clientWay: 'sell',
    clipSize: undefined,
    notional: undefined,
    notionalCurrencyIndex: 1,

    limitPrice: undefined,
    clippingMode: 'Automatic',
    isNdf: false,

    spreadCapture: true,
    liquidityPool: [...LIQUIDITY_POOLS],
    randomize: true,
    tenor: undefined,
    expiryDate: {
      goodTillDate: false,
      date: undefined,
      time: undefined,
    },
    startDate: {
      isNow: true,
      date: format(getDateNow(), 'yyyy-MM-dd'),
      time: format(getDateNow(), 'HH:mm:ss'),
    },

    notificationEmails: undefined,
  },
});

export const generateFalconOrder = (): FalconOrder => ({
  type: 'FalconOrder',
  clientId: undefined,

  properties: {
    status: { type: 'creating' },
    mode: 'VIEW',
    clockOffset: undefined,
    currencyPair: undefined,
    isReadyToSubmit: false,

    clientWay: 'sell',
    notional: undefined,
    notionalCurrencyIndex: 1,

    limitPrice: undefined,
    isNdf: false,

    liquidityPool: [...LIQUIDITY_POOLS],
    speed: 'Normal',

    tenor: undefined,
    expiryDate: {
      goodTillDate: true,
      date: undefined,
      time: undefined,
    },
    startDate: {
      isNow: true,
      date: format(getDateNow(), 'yyyy-MM-dd'),
      time: format(getDateNow(), 'HH:mm:ss'),
    },

    notificationEmails: undefined,
  },
});

export const generateNightjarOrder = (): NightjarOrder => ({
  type: 'NightjarOrder',
  clientId: undefined,

  properties: {
    status: { type: 'creating' },
    mode: 'VIEW',
    clockOffset: undefined,
    currencyPair: undefined,
    isReadyToSubmit: false,

    clientWay: 'sell',
    notional: undefined,
    notionalCurrencyIndex: 1,

    limitPrice: undefined,
    isNdf: false,

    liquidityPool: [...LIQUIDITY_POOLS],
    alphaSeeker: false,
    speed: 'Normal',

    tenor: undefined,
    expiryDate: {
      goodTillDate: true,
      date: undefined,
      time: undefined,
    },
    startDate: {
      isNow: true,
      date: format(getDateNow(), 'yyyy-MM-dd'),
      time: format(getDateNow(), 'HH:mm:ss'),
    },

    notificationEmails: undefined,
  },
});

export const generateTwapOrderFromBlotter = (entry: BlotterOrderEntry, mode: OrderMode): TwapOrder => {
  const currencyPair = getOMSCurrencyFromBlotterCurrency(entry.currencyPair);
  const startTime = entry.startTime ? new Date(entry.startTime) : getDateNow();

  return {
    id: entry.id,
    type: 'Twap+Order',
    clientId: undefined,

    properties: {
      mode,
      currencyPair,
      status: { type: 'submitted', tradeId: entry.id },

      isReadyToSubmit: false,

      clientWay: entry.yourWay === 'Buy' ? 'buy' : 'sell',
      clipSize: entry.clipSize ?? undefined,
      notional: entry.notional ?? undefined,
      notionalCurrencyIndex: entry.notionalCurrency === currencyPair?.currency1 ? 1 : 2,

      limitPrice: entry.limitPrice,
      clippingMode: entry.clippingMode as ClippingMode,
      isNdf: entry.isNdf,

      spreadCapture: entry.spreadCapture ?? false,
      liquidityPool: (entry.liquidityPool ?? '').split(','),
      randomize: entry.randomize ?? false,
      tenor: entry.maturityDate ?? undefined,
      expiryDate: {
        goodTillDate: entry.isGtc,
        date: entry.expiryDay ?? undefined,
        time: entry.expiryTime ?? undefined,
      },

      startDate: {
        isNow: false,
        date: format(startTime, 'yyyy-MM-dd'),
        time: format(startTime, 'HH:mm:ss'),
      },

      notificationEmails: undefined,
      clockOffset: 0,
    },
  };
};

export const generateFalconOrderFromBlotter = (entry: BlotterOrderEntry, mode: OrderMode): FalconOrder => {
  const currencyPair = getOMSCurrencyFromBlotterCurrency(entry.currencyPair);
  const startTime = entry.startTime ? new Date(entry.startTime) : getDateNow();

  return {
    id: entry.id,
    type: 'FalconOrder',
    clientId: undefined, // when an order is summoned from the blotter , a new tab is created with customer set to the customer of the order

    properties: {
      mode,
      currencyPair,
      status: { type: 'submitted', tradeId: entry.id },

      clockOffset: 0,
      isReadyToSubmit: false,

      clientWay: entry.yourWay === 'Buy' ? 'buy' : 'sell',
      notional: entry.notional ?? undefined,
      notionalCurrencyIndex: entry.notionalCurrency === currencyPair?.currency1 ? 1 : 2,

      limitPrice: entry.limitPrice,
      isNdf: entry.isNdf,

      liquidityPool: (entry.liquidityPool ?? '').split(','),
      speed: (entry.speed ?? 'Normal') as Speed,

      tenor: entry.maturityDate ?? undefined,
      expiryDate: {
        goodTillDate: entry.isGtc,
        date: entry.expiryDay ?? undefined,
        time: entry.expiryTime ?? undefined,
      },
      startDate: {
        isNow: false,
        date: format(startTime, 'yyyy-MM-dd'),
        time: format(startTime, 'HH:mm:ss'),
      },

      notificationEmails: undefined,
    },
  };
};

export const generateNightjarOrderFromBlotter = (entry: BlotterOrderEntry, mode: OrderMode): NightjarOrder => {
  const currencyPair = getOMSCurrencyFromBlotterCurrency(entry.currencyPair);
  const startTime = entry.startTime ? new Date(entry.startTime) : getDateNow();

  return {
    id: entry.id,
    type: 'NightjarOrder',
    clientId: undefined,

    properties: {
      mode,
      currencyPair,
      status: { type: 'submitted', tradeId: entry.id },
      clockOffset: 0,
      isReadyToSubmit: false,

      clientWay: entry.yourWay === 'Buy' ? 'buy' : 'sell',
      notional: entry.notional ?? undefined,
      notionalCurrencyIndex: entry.notionalCurrency === currencyPair?.currency1 ? 1 : 2,

      limitPrice: entry.limitPrice,
      isNdf: entry.isNdf,

      liquidityPool: (entry.liquidityPool ?? '').split(','),
      speed: (entry.speed ?? 'Normal') as Speed,
      alphaSeeker: entry.alphaSeeker ?? false,

      tenor: entry.maturityDate ?? undefined,
      expiryDate: {
        goodTillDate: entry.isGtc,
        date: entry.expiryDay ?? undefined,
        time: entry.expiryTime ?? undefined,
      },
      startDate: {
        isNow: false,
        date: format(startTime, 'yyyy-MM-dd'),
        time: format(startTime, 'HH:mm:ss'),
      },

      notificationEmails: undefined,
    },
  };
};

type BaseAlgoOrderRequest = {
  tileId: string;
  clientId: number;
  way?: ClientWay;
  notional?: number;
  notionalCurrencyIndex: 1 | 2;
  currency1: Currency;
  currency2: Currency;
  liquidityPool: string[];
  tenor?: string; // maturityDate
  startDate: StartDate;
  expiryDate: ExpiryDate; //endDateUtc
  limitPrice?: number;
  isNdf: boolean;
  notificationEmails?: {
    isOverridenEmails?: boolean;
    emails: string[];
  };
  clockOffset?: number; // offset in milliseconds between the backend and the client
};

type TwapOrderRequest = BaseAlgoOrderRequest & {
  orderType: 'Twap+Order';
  clippingMode: ClippingMode;
  clipSize?: number;
  spreadCapture: boolean;
  randomize: boolean; // dodging
};

type FalconOrderRequest = BaseAlgoOrderRequest & {
  orderType: 'FalconOrder';
  speed: Speed;
};

type NightjarOrderRequest = BaseAlgoOrderRequest & {
  orderType: 'NightjarOrder';
  speed: Speed;
  alphaSeeker: boolean;
};

export type AlgoOrderRequest = TwapOrderRequest | FalconOrderRequest | NightjarOrderRequest;

export const LIQUIDITY_POOLS = ['Markets', 'Curex', 'Internal'] as const;
export type LiquidityPool = (typeof LIQUIDITY_POOLS)[number];

export const CLIPPING_MODES = ['Automatic', 'Manual', 'Incremental', 'Decremental', 'Splayed'] as const;
export type ClippingMode = (typeof CLIPPING_MODES)[number];
